import logError from '@alltrails/core/utils/logError';
import type { POIDetailsResponse } from '@alltrails/explore-service/types/POIDetailsResponse';
import { baseApi } from '@alltrails/redux-helpers/baseApi';
import type {
  Costings,
  CostingsConfig,
  CustomRouteActivityType,
  CustomRouteAlert,
  CustomRouteMap,
  UserCustomRoute
} from '@alltrails/types/CustomRoute';
import type { MapPhoto } from '@alltrails/types/Map';
import type { ListMapTaggedUsersResponse } from '@alltrails/types/MapUser';
import type { User } from '@alltrails/types/User';
import type { CustomRouteDetails, CustomRoutePath, CustomRouteSegment, GroupedSegment } from '../types/CustomRoute';
import { CustomRoutePhoto } from '../types/CustomRoutePhoto';
import type { LatLng } from '../types/Geo';
import type MetadataActivityType from '../types/MetadataActivityType';
import { type CustomRouteResponse, MapResponse, transformCustomRouteMapResponse } from '../utils/customRoutes/transformCustomRouteMapResponse';
import { isSegmentRoutable } from '../utils/customRoutes/validation';
import { decodedObjectsFromPolyline, decodeLatLngs, encodeLatLngs } from '../utils/legacyGeoJSONConversions';

type CreateCustomRoutePayload = {
  route: CustomRouteDetails;
  trailId?: number;
};

export type AcceptMapInviteResponse = {
  success: boolean;
  at_map_tagged_user_id?: number;
  error_type?: string;
  error_message?: string;
};

type UpdateCustomRoutePayload = {
  id: number;
  route: CustomRouteDetails;
};

type PointCoordinates = {
  lat: number;
  lng: number;
};

export type ServerRoute = {
  locations: PointCoordinates[][];
};

export type GetUserCustomRoutesParams = {
  userId: number;
  presentationType: string;
  after?: string;
};

type GetUserCustomRoutesResponse = {
  maps: (UserCustomRoute & { ID: number })[];
  meta: {
    status: string;
    items: number;
    timestamp: string;
  };
  pageInfo: PageInfo;
};

type GetMetadataPayload = {
  segmentsPaths: LatLng[][][];
  activityUid?: MetadataActivityType;
};

type AverageTimeToComplete = {
  value: string;
  unit: string;
};

export type Metadata = {
  elevationGain: number | null;
  elevationLoss: number | null;
  length: number | null;
  averageTimeToComplete: AverageTimeToComplete | null;
};

export type MetadataStartLocation = {
  city: string;
  country: string;
  country_id?: number;
  latitude: number;
  longitude: number;
  postal_code?: string;
  region: string;
};

export type SegmentMetadata = {
  elevations: number[] | null;
  elevationGain: number | null;
  elevationLoss: number | null;
  length: number | null;
  averageTimeToComplete: AverageTimeToComplete | null;
  startLocation: MetadataStartLocation;
};

export type GetMetadataResponse = Metadata & {
  segmentsMetadata: SegmentMetadata[];
};

export type CustomRouteProfilePhoto = {
  id: number;
  photoHash: string;
  width: number;
  height: number;
  title: string | null;
  description: string | null;
  user: User;
};

type MetadataResponse = {
  routes: {
    smoothedElevations: string;
    metadata: {
      elevationGain: number;
      elevationLoss: number;
      elevationMin: number;
      elevationMax: number;
      length: number;
      averageTimeToComplete: AverageTimeToComplete | null;
    };
    startLocation: MetadataStartLocation;
  }[];
  photoId: number | null;
  metadata: {
    elevationGain: number;
    elevationLoss: number;
    elevationMin: number;
    elevationMax: number;
    length: number;
    averageTimeToComplete: AverageTimeToComplete | null;
  };
  includeElevation: boolean;
};

export type RoutingMode = 'hike' | 'bike' | 'mtb' | 'offroad' | 'car';

export type GetRoutePayload = {
  routingMode: RoutingMode;
  routes: ServerRoute[];
  activityTypeChange: boolean;
  // Partial because the number-based costings are only sent for the advanced routing actions that increment them,
  // while the enum-based costings are sent whenever the user has set them as preferences
  costings?: Partial<Costings>;
};

export type GetRouteResponse = {
  alerts?: CustomRouteAlert[];
  groupedSegments?: GroupedSegment[];
  costings: Costings;
  error?: RouteError;
};

type RouteError = {
  iconCode: string;
  message: string;
  errorType: string;
};

export type RouteResponse = {
  alerts?: CustomRouteAlert[];
  routes: Array<
    Array<{
      paths?: Array<{
        polyline: string;
      }>;
      error?: RouteError;
    }>
  >;
  costings: Costings;
  errors?: number;
};

export type GetInfoResponse = {
  costings: CostingsConfig;
  activityTypes: {
    id: number;
    uid: CustomRouteActivityType;
    name: string;
    routingMode: string;
    iconCode: string;
  }[];
};

type Meta = { status: string };

type PageInfo = {
  totalItemCount: number;
  itemCount: number;
  hasNextPage: boolean;
  nextCursor: string;
};

type CopyRoutePayload = {
  mapId: number;
  name: string;
  detail?: string;
};

export type GetUserCustomRouteResponse = {
  maps: CustomRouteMap[];
  meta: Meta;
  pageInfo: PageInfo;
};

export type GetCustomRouteDetailsResponse = {
  id: number;
  pointsOfInterest: {
    countLabel: string;
    results: POIDetailsResponse[];
  };
};

export const transformCustomRoute = (route: CustomRouteDetails) => {
  const { bounds, name, description, contentPrivacy, activityType, segments, waypoints: rawWaypoints, createdAt } = route;
  const location =
    segments.length > 0 && segments[0].paths.length > 0 && segments[0].paths[0].points.length > 0 ? segments[0].paths[0].points[0] : undefined;
  const routes = segments.map(({ serverId, color, paths, description, name, isLinkedToNextSegment }, segmentIndex) => ({
    id: serverId,
    description,
    name,
    sequence_num: segmentIndex + 1,
    lineDisplayProperty: { color },
    lineSegments: paths.map((path, pathIndex) => ({
      sequence_num: pathIndex + 1,
      polyline: { pointsData: encodeLatLngs(path.points) },
      freeDraw: path.freeDraw
    })),
    isLinkedToNextSegment
  }));
  const waypoints = rawWaypoints.map(
    ({ serverId, name, description, latitude, longitude, showTitle, waypointCategory, contentPrivacy, isGlobal }) => ({
      id: serverId,
      name,
      description,
      location: { latitude, longitude },
      waypointDisplayProperty: { showTitle },
      ...(waypointCategory !== undefined && { waypointCategory: { id: waypointCategory.id, uid: waypointCategory.uid } }),
      ...(contentPrivacy !== undefined && { contentPrivacy }),
      ...(isGlobal !== undefined && { isGlobal })
    })
  );

  return {
    apply_map_rules: true,
    combine_segments: false,
    detail: 'deep',
    map: {
      bounds,
      name,
      description,
      contentPrivacy: contentPrivacy || 'urn:alltrails:visibility:public',
      activity: { uid: activityType },
      location,
      routes,
      waypoints,
      presentationType: 'M',
      source: 1033,
      createdAt: createdAt || new Date().toISOString()
    }
  };
};

const transformCustomRouteForTrail = (route: CustomRouteDetails, trailId: number) => {
  const transformedRoute = transformCustomRoute(route);
  return {
    ...transformedRoute,
    system_owned: true,
    map: {
      ...transformedRoute.map,
      trailId,
      status: 'P'
    }
  };
};

/**
 * Convert an array of segments to an array of server routes with free drawn paths excluded.
 * Each location in a server route is an array of contiguous non-free-drawn paths that are separated by free-drawn paths.
 * We exclude free drawn paths because the server does not currently support treating them differently from non-free-drawn paths.
 *
 * Given the following segments:
 * [
 *   {
 *     paths: [
 *       { points: [[0, 0], [1, 1], [2, 2]], freeDraw: false },
 *       { points: [[2, 2], [3, 3], [4, 4]], freeDraw: false },
 *       { points: [[4, 4], [5, 5]], freeDraw: true },
 *       { points: [[5, 5], [6, 6], [7, 7]], freeDraw: false }
 *     ]
 *   },
 *   {
 *     paths: [
 *       { points: [[10, 10], [11, 11], [12, 12]], freeDraw: false },
 *       { points: [[12, 12], [13, 13]], freeDraw: true },
 *       { points: [[13, 13], [14, 14], [15, 15]], freeDraw: false },
 *       { points: [[15, 15], [16, 16], [17, 17]], freeDraw: false }
 *     ]
 *   }
 * ]
 *
 * The resulting server routes would be:
 * [
 *   {
 *     locations: [
 *       [{ lat: 0, lng: 0 }, { lat: 2, lng: 2 }, { lat: 4, lng: 4 }],
 *       [{ lat: 5, lng: 5 }, { lat: 7, lng: 7 }]
 *     ]
 *   },
 *   {
 *     locations: [
 *       [{ lat: 10, lng: 10 }, { lat: 12, lng: 12 }],
 *       [{ lat: 13, lng: 13 }, { lat: 15, lng: 15 }, { lat: 17, lng: 17 }],
 *     ]
 *   }
 * ]
 * @param segments An array of custom route segments
 * @returns An array of server routes whose locations are arrays of contiguous non-free-drawn paths
 */
export const transformSegmentsToServerRoutes = (segments: CustomRouteSegment[]): ServerRoute[] =>
  segments.filter(isSegmentRoutable).map(segment => {
    // Split the segment's paths into arrays of contiguous non-free-drawn paths that are separated by free-drawn paths
    let isEndOfGroup = false;
    const pathGroups = segment.paths.reduce(
      (acc, path) => {
        if (!path.freeDraw) {
          // If at the end of a non-empty group of contiguous paths, start a new group
          if (isEndOfGroup && acc[acc.length - 1].length > 0) {
            acc.push([path]);
          } else {
            acc[acc.length - 1].push(path);
          }

          isEndOfGroup = false;
        } else {
          // If the path is free-drawn, we end the current group of contiguous paths
          isEndOfGroup = true;
        }

        return acc;
      },
      [[]] as CustomRoutePath[][]
    );

    // For each group of contiguous paths, extract the bounding points and convert them to PointCoordinates
    // i.e. the first point of every path and the last point of the last path
    const locations = pathGroups.map(paths =>
      paths.reduce((acc, path, index) => {
        const firstPoint = path.points[0];
        acc.push({ lat: firstPoint[0], lng: firstPoint[1] });

        // If the path is the last path in a group of contiguous paths, add the last point of the path
        if (index === paths.length - 1) {
          const lastPoint = path.points[path.points.length - 1];
          acc.push({ lat: lastPoint[0], lng: lastPoint[1] });
        }

        return acc;
      }, [] as PointCoordinates[])
    );

    return {
      locations
    };
  });

export const transformPointsToServerRoutes = (points: LatLng[]): ServerRoute[] => [
  { locations: [points.map(point => ({ lat: point[0], lng: point[1] }))] }
];

// Transform from server types

export const transformMetadataResponse = (response: MetadataResponse): GetMetadataResponse => {
  const { routes, metadata, includeElevation } = response;
  const { elevationGain, elevationLoss, length, averageTimeToComplete } = metadata;
  const hasElevationData = includeElevation ?? true;

  const segmentsMetadata = [...routes].map(route => {
    const {
      smoothedElevations,
      metadata: { elevationGain, elevationLoss, length, averageTimeToComplete },
      startLocation
    } = route;

    const elevations =
      hasElevationData && smoothedElevations ? decodedObjectsFromPolyline(smoothedElevations, [5]).map(elevation => elevation[0]) : null;

    return {
      elevations,
      elevationGain: hasElevationData ? elevationGain : null,
      elevationLoss: hasElevationData ? elevationLoss : null,
      length,
      averageTimeToComplete,
      startLocation
    };
  });

  return {
    segmentsMetadata,
    elevationGain: hasElevationData ? elevationGain : null,
    elevationLoss: hasElevationData ? elevationLoss : null,
    length,
    averageTimeToComplete
  };
};

export const transformRouteResponse = (response: RouteResponse): GetRouteResponse => {
  const { routes, costings, errors } = response;

  // If there is an error return it without processing the routes
  if (errors) {
    for (const route of routes) {
      for (const pathGroup of route) {
        if (pathGroup.error) {
          return { error: pathGroup.error, costings };
        }
      }
    }
  }

  // Each route in the response is a segment
  const groupedSegments = routes.map(route => ({
    // Each array in the route is a group of contiguous paths
    pathGroups: route.map(pathGroup => ({
      // Each path in the pathGroup contains a polyline containing encoded points
      paths:
        pathGroup.paths?.map(path => ({
          points: decodeLatLngs(path.polyline),
          // Since the API only supports non-free-drawn paths, we set freeDraw to false
          freeDraw: false
        })) || []
    }))
  }));

  return { alerts: response.alerts, groupedSegments, costings };
};

export const customRoutesApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['UserCustomRoutes', 'CustomRouteDetails', 'MapTaggedUsers'] })
  .injectEndpoints({
    endpoints: builder => ({
      createCustomRoute: builder.mutation<CustomRouteResponse, CreateCustomRoutePayload>({
        query: ({ route, trailId }) => {
          const url = '/api/alltrails/maps';
          const method = 'post';
          const data = trailId ? transformCustomRouteForTrail(route, trailId) : transformCustomRoute(route);

          return { url, method, data };
        },
        transformResponse: transformCustomRouteMapResponse
      }),
      updateCustomRoute: builder.mutation<CustomRouteResponse, UpdateCustomRoutePayload>({
        query: ({ id, route }) => {
          const url = `/api/alltrails/maps/${id}`;
          const method = 'put';
          const data = transformCustomRoute(route);

          return { url, method, data };
        },
        transformResponse: transformCustomRouteMapResponse,
        invalidatesTags: (_, __, { id }) => [{ type: 'CustomRouteDetails', id }]
      }),
      getMetadata: builder.query<GetMetadataResponse, GetMetadataPayload>({
        query: ({ activityUid, segmentsPaths }) => {
          const url = `/api/alltrails/geo-service/v2/routing/metadata`;
          const method = 'post';
          const routes = segmentsPaths.map(segmentPaths => ({ paths: segmentPaths.map(path => ({ polyline: encodeLatLngs(path) })) }));
          // The endpoint requires a string, even if just an empty one, This happens when the chosed BYOT activity type is not supported by the metadata endpoint
          return { url, method, data: { activityUid: activityUid ?? '', routes } };
        },
        transformResponse: transformMetadataResponse
      }),
      getRoute: builder.query<GetRouteResponse, GetRoutePayload>({
        query: ({ routingMode, routes, activityTypeChange, costings }) => {
          const url = `/api/alltrails/geo-service/v2/routing/route`;
          const method = 'post';
          return {
            url,
            method,
            data: { routingMode, routes, activityTypeChange, costings }
          };
        },
        transformResponse: transformRouteResponse
      }),
      getUserCustomRoutes: builder.query<GetUserCustomRoutesResponse, GetUserCustomRoutesParams>({
        query: ({ userId, presentationType = 'map', after }) => {
          const params = new URLSearchParams({
            detail: 'algolia',
            presentation_type: presentationType,
            limit: '100',
            ...(after && { after })
          });

          return {
            url: `/api/alltrails/users/${userId}/maps?${params.toString()}`,
            method: 'get'
          };
        },
        transformResponse: (response: GetUserCustomRoutesResponse) => {
          const transformedMaps = response.maps.map(map => ({
            ...map,
            id: map.ID
          }));
          return {
            ...response,
            maps: transformedMaps
          };
        },
        merge: (currentCache, results, { arg }: { arg: GetUserCustomRoutesParams }) => {
          if (!arg.after) {
            currentCache.maps = [...results.maps];
          } else {
            currentCache.maps = [...currentCache.maps, ...results.maps];
          }
          currentCache.meta = { ...results.meta };
          currentCache.pageInfo = { ...results.pageInfo };
        },
        providesTags: result => (result ? result.maps.map(map => ({ type: 'UserCustomRoutes', id: map.ID })) : [])
      }),
      getUserCustomRoute: builder.query<CustomRouteResponse[], number[]>({
        async queryFn(routeIds, _queryApi, _extraOptions, fetchWithBQ) {
          // Make all requests in parallel
          const results = await Promise.all(
            routeIds.map(routeId => fetchWithBQ({ url: `/api/alltrails/maps/${routeId}?detail=deep&`, method: 'get' }))
          );
          // Check for errors
          const errored = results.find(r => r.error);
          if (errored && errored.error) {
            return { error: errored.error };
          }
          // Transform each response if needed
          const data = results.map(r => transformCustomRouteMapResponse(r.data as MapResponse));
          return { data };
        }
      }),
      deleteMap: builder.mutation<{ meta: Meta }, { mapId: number }>({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}`,
          method: 'delete'
        }),
        invalidatesTags: (_, __, { mapId }) => [{ type: 'UserCustomRoutes', id: mapId }], // Invalidate the specific map and the list
        async onQueryStarted(_, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (error) {
            logError(new Error('[MapApi] error when deleting a map', { cause: error }));
          }
        }
      }),
      // Admin-only (trails:manage): re-processes polylines and recalculates route stats.
      refreshMapCache: builder.mutation<{ meta: Meta }, { mapId: number }>({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}/refresh_map_cache`,
          method: 'get'
        })
      }),
      editEndpoint: builder.mutation<CustomRouteResponse, { mapId: number; atLineSegmentId: number; cropIndex: number; newEndPoint?: LatLng }>({
        query: ({ mapId, atLineSegmentId, cropIndex, newEndPoint }) => ({
          url: `/api/alltrails/maps/${mapId}/endpoint/edit`,
          method: 'put',
          data: {
            at_line_segment_id: atLineSegmentId,
            crop_idx: cropIndex,
            ...(newEndPoint ? { new_end_pt: newEndPoint } : {})
          }
        }),
        transformResponse: transformCustomRouteMapResponse,
        invalidatesTags: (_, __, { mapId }) => [{ type: 'CustomRouteDetails', id: mapId }]
      }),
      // Admin-only (trails:manage): moderation flag hiding the map/recording from public view.
      setMapHidden: builder.mutation<CustomRouteResponse, { mapId: number; hidden: boolean }>({
        query: ({ mapId, hidden }) => ({
          url: `/api/alltrails/maps/${mapId}/metadata`,
          method: 'post',
          data: { hidden, detail: 'deep' }
        }),
        transformResponse: transformCustomRouteMapResponse
      }),
      getStaticMapUrl: builder.query<{ url: string }, { segmentPaths: CustomRoutePath[][]; size: string }>({
        query: ({ segmentPaths, size }) => {
          const method = 'post';
          const paths = segmentPaths
            .map(path => {
              const points = path.map(p => p.points).flat();
              if (points.length < 2) {
                return null;
              }
              const polyline = encodeLatLngs(points);
              return polyline ? { polyline } : null;
            })
            .filter(Boolean);
          return { url: `/api/alltrails/polylines/static_map`, config: { params: { direct_link: 'true' } }, method, data: { paths, size } };
        }
      }),
      getInfo: builder.query<GetInfoResponse, void>({
        query: () => ({
          url: '/api/alltrails/geo-service/routing/info',
          method: 'get'
        })
      }),
      copyRoute: builder.mutation<CustomRouteResponse, CopyRoutePayload>({
        query: ({ mapId, name, detail = 'deep' }) => ({
          url: `/api/alltrails/maps/${mapId}/copy`,
          method: 'post',
          data: { detail, name }
        }),
        transformResponse: transformCustomRouteMapResponse,
        invalidatesTags: ['UserCustomRoutes']
      }),
      getProfilePhoto: builder.query<{ profile_photo: CustomRouteProfilePhoto; count: number }, { segmentsPaths: LatLng[][][]; isMobile?: boolean }>({
        query: ({ segmentsPaths, isMobile }) => {
          const method = 'post';
          const routes = segmentsPaths.map(segmentPaths => ({
            paths: segmentPaths.map(path => ({ polyline: encodeLatLngs(path) }))
          }));
          return {
            url: '/api/alltrails/geo-service/routing/profile_photo',
            method,
            data: { routes, landscape: !isMobile }
          };
        }
      }),
      getCustomRoutePhotos: builder.query<
        {
          photos: CustomRoutePhoto[];
          metadata: { itemCount: number };
        },
        { mapId?: number }
      >({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}/photos/carousel?landscape=true`,
          method: 'get'
        })
      }),
      getCustomRouteDetails: builder.query<GetCustomRouteDetailsResponse, { mapId: number }>({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}/custom_route_details`,
          method: 'get'
        }),
        providesTags: (_, __, { mapId }) => [{ type: 'CustomRouteDetails', id: mapId }]
      }),
      getMapTaggedUsers: builder.query<ListMapTaggedUsersResponse, { mapId: number }>({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}/tagged_users`,
          method: 'get'
        }),
        providesTags: (_, __, { mapId }) => [{ type: 'MapTaggedUsers', id: mapId }]
      }),
      acceptMapInvite: builder.mutation<AcceptMapInviteResponse, { mapId: number; invitationToken: string }>({
        query: ({ mapId, invitationToken }) => ({
          url: `/api/alltrails/maps/${mapId}/tagged_users`,
          method: 'post',
          data: { invitation_token: invitationToken }
        }),
        // Refetch tagged users so the joiner appears without a reload.
        invalidatesTags: (_, __, { mapId }) => [{ type: 'MapTaggedUsers', id: mapId }]
      }),
      // Fetches the latest map and keeps only its photos, so an upload can be reflected in redux
      // without a full page reload. transformResponse discards the rest of the (deep) map payload.
      getRecordingMapPhotos: builder.query<MapPhoto[], { mapId: number }>({
        query: ({ mapId }) => ({
          url: `/api/alltrails/maps/${mapId}?detail=deep&combine_segments=false`,
          method: 'get'
        }),
        transformResponse: (response: MapResponse) => response.maps?.[0]?.mapPhotos ?? []
      })
    })
  });

export const {
  useCreateCustomRouteMutation,
  useUpdateCustomRouteMutation,
  useGetMetadataQuery,
  useLazyGetMetadataQuery,
  useLazyGetUserCustomRoutesQuery,
  useDeleteMapMutation,
  useRefreshMapCacheMutation,
  useSetMapHiddenMutation,
  useEditEndpointMutation,
  useLazyGetStaticMapUrlQuery,
  useGetInfoQuery,
  useLazyGetRouteQuery,
  useCopyRouteMutation,
  useLazyGetProfilePhotoQuery,
  useGetUserCustomRouteQuery,
  useGetCustomRoutePhotosQuery,
  useGetCustomRouteDetailsQuery,
  useGetMapTaggedUsersQuery,
  useAcceptMapInviteMutation,
  useLazyGetRecordingMapPhotosQuery
} = customRoutesApi;
